<template>
  <div class="mpid-device-details-info">
    <!-- Device details section -->
    <div class="section-content">
      <div class="section-header section-header-alt">
        <div class="section-title">DEVICE DETAILS :</div>
        <div class="action-buttons-container">
          <!-- Uninstall Device button - styled like Uninstall Meter -->
          <button
              class="action-button remove-meter-button"
              v-if="!isLoading && device"
              @click="uninstallDevice"
              :disabled="loading"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 6h18"></path>
              <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"></path>
            </svg>
            {{ loading ? 'Uninstalling...' : 'De-touch Device' }}
          </button>
        </div>
      </div>

      <!-- Loading state -->
      <div v-if="isLoading" class="loading-state">
        <div class="loading-spinner"></div>
        <span>Loading device details...</span>
      </div>

      <!-- Error state -->
      <div v-else-if="error" class="error-state">
        <div class="error-message">{{ error }}</div>
        <button class="retry-button" @click="$emit('retry')">Retry</button>
      </div>

      <!-- Content state -->
      <div v-else-if="device" class="device-details-grid">
        <div class="row">
          <!-- Left Column -->
          <div class="col-6">
            <div class="device-info-row">
              <div class="device-info-label">LDN :</div>
              <div class="device-info-value">
                <router-link
                    v-if="device.logical_device_number"
                    :to="`/devices/${device.logical_device_number}`"
                    class="ldn-link "
                    title="Open device details"
                >
                  {{ device.logical_device_number }}
                </router-link>
              </div>
            </div>
            <div class="device-info-row">
              <div class="device-info-label">TYPE :</div>
              <div class="device-info-value">{{ device.type || 'N/A' }}</div>
            </div>
            <div class="device-info-row">
              <div class="device-info-label">CALIBER :</div>
              <div class="device-info-value">{{ device.caliber || 'N/A' }}</div>
            </div>
            <!-- New section for displaying active device relationships based on type -->
            <div class="device-info-row" v-if="device.type === 'MECHANICAL'">
              <div class="device-info-label">ACTIVE RADIO :</div>
              <div class="device-info-value">{{ hasActiveRadio ? getLastActiveRadioLDN : 'N/A' }}</div>
            </div>
            <div class="device-info-row" v-if="device.type === 'ADDON'">
              <div class="device-info-label">ACTIVE METER :</div>
              <div class="device-info-value">{{ hasActiveMeter ? getLastActiveMeterLDN : 'N/A' }}</div>
            </div>
            <div class="device-info-row" v-if="device.id_radio">
              <div class="device-info-label">RADIO ID :</div>
              <div class="device-info-value">{{ device.id_radio }}</div>
            </div>
            <div class="device-info-row" v-if="device.lora_eui">
              <div class="device-info-label">LORA EUI :</div>
              <div class="device-info-value">{{ device.lora_eui }}</div>
            </div>
            <div class="device-info-row" v-if="device.removed">
              <div class="device-info-label">REMOVED DATE :</div>
              <div class="device-info-value">{{ formatDate(device.removed) }}</div>
            </div>
          </div>

          <!-- Right Column -->
          <div class="col-6">
            <div class="device-info-row">
              <div class="device-info-label">CUSTOMER NUMBER :</div>
              <div class="device-info-value">{{ device.customer_number || 'N/A' }}</div>
            </div>
            <div class="device-info-row">
              <div class="device-info-label">PROTOCOL :</div>
              <div class="device-info-value">{{ device.protocol || 'N/A' }}</div>
            </div>
            <div class="device-info-row">
              <div class="device-info-label">INSTALLED :</div>
              <div class="device-info-value">{{ formatDate(device.installed) }}</div>
            </div>
            <div class="device-info-row" v-if="device.is_integrated !== undefined">
              <div class="device-info-label">INTEGRATED :</div>
              <div class="device-info-value" :class="{'status-yes': device.is_integrated, 'status-no': !device.is_integrated}">
                {{ device.is_integrated ? 'Yes' : 'No' }}
              </div>
            </div>
            <div class="device-info-row" v-if="device.distribution_company">
              <div class="device-info-label">DISTRIBUTION COMPANY :</div>
              <div class="device-info-value">{{ device.distribution_company }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-else class="empty-state">
        <div class="info-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>
        <p>No device information available.</p>
        <p class="secondary-text">This metering point is not attached to any device.</p>
        <button class="action-button action-button-primary mt-3"
                @click="$emit('open-attach-device')">
          Attach to Device
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import DeviceApiService from '@/services/DeviceApiServices';

export default {
  name: 'MpidDeviceDetailsInfo',
  props: {
    device: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
    error: { type: String, default: '' }
  },
  data() {
    return {
      loading: false
    };
  },
  computed: {
    hasActiveRadio() {
      const arr = this.device?.last_active_radio;
      if (!Array.isArray(arr) || arr.length === 0) return false;
      return arr.some(r => !r.to_time && !r.totime);
    },
    hasActiveMeter() {
      const arr = this.device?.last_active_meter;
      if (!Array.isArray(arr) || arr.length === 0) return false;
      return arr.some(m => !m.to_time && !m.totime);
    },
    getLastActiveRadioLDN() {
      if (!this.hasActiveRadio) return 'N/A';
      const current = this.device.last_active_radio.find(r => !r.to_time && !r.totime);
      return current?.logical_device_number || 'N/A';
    },
    getLastActiveMeterLDN() {
      if (!this.hasActiveMeter) return 'N/A';
      const current = this.device.last_active_meter.find(m => !m.to_time && !m.totime);
      return current?.logical_device_number || 'N/A';
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return 'Invalid Date';
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    },
    async uninstallDevice() {
      if (!this.device?.logical_device_number) {
        return;
      }

      this.loading = true;

      try {
        // Use DeviceApiService instead of direct fetch
        await DeviceApiService.removeFromMPID(this.device.logical_device_number);

        // Emit event to notify parent component that device was detached from MPID
        this.$emit('device-detached', this.device.logical_device_number);

        // Trigger a retry/refresh of the parent data
        this.$emit('retry');
      } catch (err) {
        console.error('Error detaching device from MPID:', err);
        // Could emit an error event if needed
        // this.$emit('error', 'Failed to detach device from MPID');
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
<style scoped>
.mpid-device-details-info {
  height: 100%;
}

.section-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-header-alt {
  margin-top: 80px;
}

.section-title {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: white;
  display: inline-block;
}

.section-subtitle {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 15px;
  display: inline-block;
}

.device-details-grid {
  margin-top: 35px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.device-info-row {
  margin-bottom: 25px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.device-info-label {
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.05em;
  opacity: 0.6;
  text-align: left;
  text-transform: uppercase;
}

.device-info-value {
  font-size: 11px;
  font-weight: 500;
}

.status-yes {
  color: #4caf50 !important;
}

.status-no {
  color: #f44336 !important;
}

/* Loading state styles */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 15px;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top: 3px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.action-button:hover:not(:disabled) {
  background-color: rgba(255,255,255,0.1);
}

.action-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.action-button-primary {
  background-color: rgba(63,81,181,0.2);
  border: 1px solid rgba(63,81,181,0.5);
}

.action-button-primary:hover {
  background-color: rgba(63,81,181,0.3);
}

/* Remove Meter Button Styling - matches DeviceDetailsInfo.vue */
.remove-meter-button {
  background: none;
  border: none;
  color: white;
}

.remove-meter-button:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.1);
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error state styles */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 15px;
}

.error-message {
  color: #ff8a80;
  text-align: center;
}

.retry-button {
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.retry-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Empty state styles */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  opacity: 0.8;
  text-align: center;
}

.info-icon {
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 16px;
}

.empty-state p {
  font-size: 13px;
  max-width: 80%;
  margin-bottom: 8px;
}

.secondary-text {
  font-size: 11px !important;
  opacity: 0.7;
}

/* Responsive adjustments */
@media (max-width: 767.98px) {
  .section-header-alt {
    margin-top: 60px;
  }

  .device-info-row {
    margin-bottom: 15px;
  }
}

.action-buttons-container {
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: center;
}

.mt-3 {
  margin-top: 16px;
}

.ldn-link {
  color: #4fc3f7;
  cursor: pointer;
  text-decoration: none;

}
</style>